import {
  buildTopologyEdgeRef,
  buildTopologyFaceRef,
  buildTopologySubEntityId,
  buildTopologyVertexRef,
  DEFAULT_TOPOLOGY_REF_TOLERANCE
} from '../../topology/TopologyReferenceBuilders';
import {
  resolveEdgeCurveKindFromPuppyKernel,
  resolveFaceSurfaceKindFromPuppyKernel,
  sampleEdgeCurveFromPuppyKernel,
} from './topologyCurveSurfaceAnalysis';
import {resolveEdgeReferencePoints} from './topologyEdgeReferencePoints';
import {isClosedSeamEdgeAcrossFacesFromPuppyKernel} from './topologySeamEdgeAnalysis';
import {arePuppyShapesSame, deduplicatePuppyShapes} from './puppyShapeIdentity';

import {isPuppyFaceReversed} from './puppyFaceOrientation';

type PuppyKernel = ReturnType<typeof import('../../worker/engine/loader').getPuppyKernel>;

type EdgeAccumulator = {
  vertexAIndex: number;
  vertexBIndex: number;
  adjacentFaces: Set<number>;
  usageByFace: Map<number, number>;
};

type RawPoint3D = [number, number, number];

type FaceIdentityRecord = {
  faceIndex: number;
  triangleRange: [number, number];
  localPoint: RawPoint3D;
  localNormal: RawPoint3D;
  persistentRef: string;
  subEntityId: string;
  semanticName?: string;
  owningFeatureId: string | null;
  shapeHash?: number;
};

type AuthorityTopologyBuildOptions = {
  /** Puppy CAD 内核 实例。 */
  puppy?: PuppyKernel;
  /** 当前正式结果对应的 Puppy CAD 内核 形体。 */
  shape?: unknown;
  /** Puppy CAD 内核 真拓扑不可用时是否允许回退为三角网格拓扑。 */
  allowMeshFallback?: boolean;
};

type ShapeKind = 'face' | 'edge' | 'vertex';

type ShapeIdentity = Record<string, unknown> & {
  HashCode?: (upperBound: number) => number;
};

type ShapeWithDelete = {
  delete?: () => void;
};

const TOPOLOGY_SHAPE_HASH_UPPER_BOUND = 2147483647;

/**
 * 安全释放 Puppy CAD 内核 对象。
 *
 * @param value 待释放对象。
 */
function safeDelete(value: unknown): void {
  const deleteMethod = (value as ShapeWithDelete | null)?.delete;
  if (typeof deleteMethod !== 'function') {
    return;
  }
  try {
    deleteMethod.call(value);
  } catch {
    // Puppy CAD 内核 清理失败不应覆盖主流程错误。
  }
}

/**
 * 判断两个 Puppy CAD 内核 Shape 是否是同一拓扑项。
 *
 * @param left 左侧形体。
 * @param right 右侧形体。
 * @returns 是否等价。
 */
const areShapesEquivalent = arePuppyShapesSame;

/**
 * 解析 Puppy CAD 内核 形体哈希。
 *
 * @param shape Puppy CAD 内核 形体。
 * @returns 哈希值；不可用时返回 `undefined`。
 */
function resolveShapeHash(shape: unknown): number | undefined {
  const hashCode = (shape as ShapeIdentity | null)?.HashCode;
  if (typeof hashCode !== 'function') {
    return undefined;
  }
  try {
    const hashValue = hashCode.call(shape, TOPOLOGY_SHAPE_HASH_UPPER_BOUND);
    return Number.isInteger(hashValue) ? hashValue : undefined;
  } catch {
    return undefined;
  }
}

/**
 * 枚举 Puppy CAD 内核 子拓扑。
 *
 * @param puppy Puppy CAD 内核 实例。
 * @param shape 目标形体。
 * @param kind 子拓扑种类。
 * @returns 子拓扑列表。
 */
function enumerateShapeItemsFromPuppyKernel(
  puppy: PuppyKernel,
  shape: unknown,
  kind: ShapeKind,
): unknown[] {
  if (!shape) {
    return [];
  }

  const explorerCtor = (puppy as Record<string, unknown>).TopExp_Explorer_2 as
    | (new (shapeArg: unknown, kindArg: unknown, stopKindArg: unknown) => {
      More: () => boolean;
      Current: () => unknown;
      Next: () => void;
      delete?: () => void;
    })
    | undefined;
  const topAbs = (puppy as Record<string, unknown>).TopAbs_ShapeEnum as Record<string, unknown> | undefined;
  const topoDs = (puppy as Record<string, unknown>).TopoDS as Record<string, ((shapeArg: unknown) => unknown)> | undefined;
  if (!explorerCtor || !topAbs) {
    return [];
  }

  const kindToken = kind === 'face'
    ? topAbs.TopAbs_FACE
    : kind === 'edge'
      ? topAbs.TopAbs_EDGE
      : topAbs.TopAbs_VERTEX;
  const wrapperName = kind === 'face' ? 'Face' : kind === 'edge' ? 'Edge' : 'Vertex';
  const wrapper = topoDs?.[wrapperName];
  const explorer = new explorerCtor(shape, kindToken, topAbs.TopAbs_SHAPE);
  const items: unknown[] = [];

  try {
    while (explorer.More()) {
      const current = explorer.Current();
      items.push(typeof wrapper === 'function' ? wrapper(current) : current);
      explorer.Next();
    }
  } finally {
    safeDelete(explorer);
  }

  return items;
}

/**
 * 去重 Puppy CAD 内核 子拓扑包装对象。
 *
 * @param items Puppy CAD 内核 子拓扑集合。
 * @returns 去重后的集合。
 */
const deduplicateShapeItems = deduplicatePuppyShapes;

/**
 * 提取 Puppy CAD 内核 顶点坐标。
 *
 * @param puppy Puppy CAD 内核 实例。
 * @param vertexShape 顶点形体。
 * @returns 顶点坐标；失败时返回 `null`。
 */
function extractVertexPointFromPuppyKernel(
  puppy: PuppyKernel,
  vertexShape: unknown,
): RawPoint3D | null {
  const pointGetter = ((puppy as Record<string, unknown>).BRep_Tool as Record<string, unknown> | undefined)?.Pnt as
    | ((vertex: unknown) => { X: () => number; Y: () => number; Z: () => number; delete?: () => void })
    | undefined;
  if (typeof pointGetter !== 'function') {
    return null;
  }
  const point = pointGetter.call((puppy as Record<string, unknown>).BRep_Tool, vertexShape);
  try {
    return [point.X(), point.Y(), point.Z()];
  } finally {
    safeDelete(point);
  }
}

/**
 * 提取 Puppy CAD 内核 边的端点坐标。
 *
 * @param puppy Puppy CAD 内核 实例。
 * @param edgeShape 边形体。
 * @returns 两端点坐标。
 */
function extractEdgeVerticesFromPuppyKernel(
  puppy: PuppyKernel,
  edgeShape: unknown,
): RawPoint3D[] {
  return enumerateShapeItemsFromPuppyKernel(puppy, edgeShape, 'vertex')
    .slice(0, 2)
    .map((vertexShape) => extractVertexPointFromPuppyKernel(puppy, vertexShape))
    .filter((point): point is RawPoint3D => Array.isArray(point));
}

/**
 * 解析 Puppy CAD 内核 面签名。
 *
 * @param puppy Puppy CAD 内核 实例。
 * @param faceShape 面形体。
 * @returns 面签名；失败时返回 `null`。
 */
function resolveFaceSignatureFromPuppyKernel(
  puppy: PuppyKernel,
  faceShape: unknown,
): {
  point: RawPoint3D;
  normal: RawPoint3D;
} | null {
  let surface: unknown = null;
  let point: unknown = null;
  let tangentU: unknown = null;
  let tangentV: unknown = null;

  try {
    surface = new puppy.BRepAdaptor_Surface_2(faceShape as never, true);
    const surfaceValue = surface as {
      FirstUParameter: () => number;
      LastUParameter: () => number;
      FirstVParameter: () => number;
      LastVParameter: () => number;
      D1: (u: number, v: number, pointArg: unknown, d1u: unknown, d1v: unknown) => void;
    };
    const u = (surfaceValue.FirstUParameter() + surfaceValue.LastUParameter()) / 2;
    const v = (surfaceValue.FirstVParameter() + surfaceValue.LastVParameter()) / 2;
    point = new puppy.gp_Pnt_3(0, 0, 0);
    tangentU = new puppy.gp_Vec_4(0, 0, 0);
    tangentV = new puppy.gp_Vec_4(0, 0, 0);
    surfaceValue.D1(u, v, point, tangentU, tangentV);

    const pointValue = point as { X: () => number; Y: () => number; Z: () => number };
    const tangentUValue = tangentU as { X: () => number; Y: () => number; Z: () => number };
    const tangentVValue = tangentV as { X: () => number; Y: () => number; Z: () => number };
    const normalX = tangentUValue.Y() * tangentVValue.Z() - tangentUValue.Z() * tangentVValue.Y();
    const normalY = tangentUValue.Z() * tangentVValue.X() - tangentUValue.X() * tangentVValue.Z();
    const normalZ = tangentUValue.X() * tangentVValue.Y() - tangentUValue.Y() * tangentVValue.X();
    const normalLength = Math.sqrt(normalX * normalX + normalY * normalY + normalZ * normalZ);

    /**
     * 参数曲面切向叉乘尚未包含 Puppy CAD 内核 面的拓扑方向。
     * 背面标记为 `TopAbs_REVERSED` 时必须整体翻转，保证快照保存实体外法向。
     */
    const orientationScale = isPuppyFaceReversed(puppy, faceShape) ? -1 : 1;


      return {
      point: [pointValue.X(), pointValue.Y(), pointValue.Z()],
      normal: normalLength > Number.EPSILON
        ? [
            normalX === 0 ? 0 : (normalX / normalLength) * orientationScale,
            normalY === 0 ? 0 : (normalY / normalLength) * orientationScale,
            normalZ === 0 ? 0 : (normalZ / normalLength) * orientationScale,
          ]
        : [0, 0, orientationScale],
    };
  } catch {
    return null;
  } finally {
    safeDelete(surface);
    safeDelete(point);
    safeDelete(tangentU);
    safeDelete(tangentV);
  }
}

/**
 * 构建顶点量化键。
 *
 * @param point 顶点坐标。
 * @returns 量化后的顶点键。
 */
function buildVertexKey(point: RawPoint3D): string {
  return `${Math.round(point[0] / DEFAULT_TOPOLOGY_REF_TOLERANCE)}:${Math.round(point[1] / DEFAULT_TOPOLOGY_REF_TOLERANCE)}:${Math.round(point[2] / DEFAULT_TOPOLOGY_REF_TOLERANCE)}`;
}

/**
 * 读取三角形顶点原始索引。
 *
 * @param meshData 网格数据。
 * @param triangleIndex 三角形索引。
 * @param vertexOffset 顶点偏移。
 * @returns 原始顶点索引。
 */
function readTriangleVertexRawIndex(
  meshData: Pick<MeshData, 'indices' | 'positions'>,
  triangleIndex: number,
  vertexOffset: 0 | 1 | 2,
): number {
  if (meshData.indices) {
    return meshData.indices[triangleIndex * 3 + vertexOffset] ?? -1;
  }
  return triangleIndex * 3 + vertexOffset;
}

/**
 * 读取原始顶点坐标。
 *
 * @param positions 顶点缓冲。
 * @param rawIndex 原始顶点索引。
 * @returns 顶点坐标。
 */
function readRawVertexPoint(
  positions: Float32Array,
  rawIndex: number,
): RawPoint3D {
  const offset = rawIndex * 3;
  return [
    positions[offset] ?? 0,
    positions[offset + 1] ?? 0,
    positions[offset + 2] ?? 0,
  ];
}

/**
 * 归一化面范围。
 *
 * @param faceTriangleRanges 输入范围。
 * @param triangleCount 三角形数量。
 * @returns 归一化后的范围。
 */
function normalizeFaceRanges(
  faceTriangleRanges: [number, number][] | undefined,
  triangleCount: number,
): [number, number][] {
  if (!faceTriangleRanges || faceTriangleRanges.length === 0) {
    return [[0, triangleCount]];
  }
  return faceTriangleRanges
    .map(([start, end]) => {
      const normalizedStart = Math.max(0, Math.min(triangleCount, Math.floor(start)));
      const normalizedEnd = Math.max(normalizedStart, Math.min(triangleCount, Math.floor(end)));
      return [normalizedStart, normalizedEnd] as [number, number];
    })
    .filter(([start, end]) => end > start);
}

/**
 * 为 Puppy CAD 内核 真拓扑对齐归一化面范围。
 *
 * 说明：
 * 1. BRepMesh 在部分平面 cap 上可能返回空三角范围；
 * 2. 这些空范围不应导致 Puppy CAD 内核 真拓扑整体回退到三角网格假拓扑；
 * 3. 只有当原始范围数量与 Puppy CAD 内核 面数量一致时才保留空范围，否则继续使用旧的非空范围逻辑。
 *
 * @param faceTriangleRanges 输入范围。
 * @param triangleCount 三角形数量。
 * @param expectedFaceCount Puppy CAD 内核 面数量。
 * @returns 可用于 Puppy CAD 内核 面对齐的范围。
 */
function normalizeFaceRangesForPuppyKernel(
  faceTriangleRanges: [number, number][] | undefined,
  triangleCount: number,
  expectedFaceCount: number,
): [number, number][] {
  if (faceTriangleRanges && faceTriangleRanges.length === expectedFaceCount) {
    return faceTriangleRanges.map(([start, end]) => {
      const normalizedStart = Math.max(0, Math.min(triangleCount, Math.floor(start)));
      const normalizedEnd = Math.max(normalizedStart, Math.min(triangleCount, Math.floor(end)));
      return [normalizedStart, normalizedEnd] as [number, number];
    });
  }
  return normalizeFaceRanges(faceTriangleRanges, triangleCount);
}

/**
 * 构建面法向。
 *
 * @param meshData 网格数据。
 * @param triangleIndex 三角形索引。
 * @returns 单位面法向。
 */
function buildFaceNormal(
  meshData: Pick<MeshData, 'positions' | 'indices'>,
  triangleIndex: number,
): RawPoint3D {
  const rawIndexA = readTriangleVertexRawIndex(meshData, triangleIndex, 0);
  const rawIndexB = readTriangleVertexRawIndex(meshData, triangleIndex, 1);
  const rawIndexC = readTriangleVertexRawIndex(meshData, triangleIndex, 2);
  const pointA = readRawVertexPoint(meshData.positions, rawIndexA);
  const pointB = readRawVertexPoint(meshData.positions, rawIndexB);
  const pointC = readRawVertexPoint(meshData.positions, rawIndexC);
  const edgeAX = pointB[0] - pointA[0];
  const edgeAY = pointB[1] - pointA[1];
  const edgeAZ = pointB[2] - pointA[2];
  const edgeBX = pointC[0] - pointA[0];
  const edgeBY = pointC[1] - pointA[1];
  const edgeBZ = pointC[2] - pointA[2];
  const normalX = edgeAY * edgeBZ - edgeAZ * edgeBY;
  const normalY = edgeAZ * edgeBX - edgeAX * edgeBZ;
  const normalZ = edgeAX * edgeBY - edgeAY * edgeBX;
  const length = Math.sqrt(normalX * normalX + normalY * normalY + normalZ * normalZ);
  if (length <= Number.EPSILON) {
    return [0, 0, 1];
  }
  return [normalX / length, normalY / length, normalZ / length];
}

/**
 * 构建面代表点。
 *
 * @param meshData 网格数据。
 * @param triangleRange 面范围。
 * @returns 面代表点。
 */
function buildFacePoint(
  meshData: Pick<MeshData, 'positions' | 'indices'>,
  triangleRange: [number, number],
): RawPoint3D {
  const rawVertexIndices = new Set<number>();
  for (let triangleIndex = triangleRange[0]; triangleIndex < triangleRange[1]; triangleIndex += 1) {
    rawVertexIndices.add(readTriangleVertexRawIndex(meshData, triangleIndex, 0));
    rawVertexIndices.add(readTriangleVertexRawIndex(meshData, triangleIndex, 1));
    rawVertexIndices.add(readTriangleVertexRawIndex(meshData, triangleIndex, 2));
  }

  const accumulator: RawPoint3D = [0, 0, 0];
  if (rawVertexIndices.size <= 0) {
    return accumulator;
  }

  rawVertexIndices.forEach((rawIndex) => {
    const point = readRawVertexPoint(meshData.positions, rawIndex);
    accumulator[0] += point[0];
    accumulator[1] += point[1];
    accumulator[2] += point[2];
  });
  return [
    accumulator[0] / rawVertexIndices.size,
    accumulator[1] / rawVertexIndices.size,
    accumulator[2] / rawVertexIndices.size,
  ];
}

/**
 * 解析拓扑出处映射。
 *
 * @param metadata 元数据。
 * @returns 拓扑出处映射；不存在时返回 `null`。
 */
function resolveTopologyProvenance(
  metadata: Record<string, unknown> | undefined,
): SerializedTopologyProvenanceMap | null {
  const candidate = metadata?.topologyProvenance as SerializedTopologyProvenanceMap | undefined;
  if (!candidate || !Array.isArray(candidate.faces) || !Array.isArray(candidate.edges)) {
    return null;
  }
  return candidate;
}

/**
 * 解析面出处项。
 *
 * 设计说明：
 * 1. 优先按当前几何签名命中，避免不同面的旧出处误绑；
 * 2. 只有几何签名变化后才按拓扑索引兜底，从而支撑草图尺寸变化后的稳定重建；
 * 3. 命中旧出处时只回填稳定身份与 owningFeatureId，当前快照的 `ref` 仍使用最新几何签名。
 *
 * @param topologyProvenance 拓扑出处映射。
 * @param persistentRef 当前面几何签名。
 * @param faceIndex 当前面索引。
 * @returns 命中的出处项；未命中时返回 `null`。
 */
function resolveFaceProvenanceEntry(
  topologyProvenance: SerializedTopologyProvenanceMap | null,
  persistentRef: string,
  faceIndex: number,
) {
  return topologyProvenance?.faces.find((entry) => (
    entry.ref === persistentRef
  )) ?? topologyProvenance?.faces.find((entry) => entry.faceIndex === faceIndex) ?? null;
}

/**
 * 解析边出处项。
 *
 * 设计说明：
 * 1. 边几何 ref 会随着尺寸变化而改变，不能只按 ref 回填出处；
 * 2. 先按 ref 精确命中，失败后按稳定拓扑索引兜底回填身份；
 * 3. 当前快照中的 `ref` 必须保持为最新几何 ref，供下游定义重写到最新几何。
 *
 * @param topologyProvenance 拓扑出处映射。
 * @param persistentRef 当前边几何 ref。
 * @param edgeIndex 当前边索引。
 * @returns 命中的出处项；未命中时返回 `null`。
 */
function resolveEdgeProvenanceEntry(
  topologyProvenance: SerializedTopologyProvenanceMap | null,
  persistentRef: string,
  edgeIndex: number,
) {
  return topologyProvenance?.edges.find((entry) => (
    entry.ref === persistentRef
  )) ?? topologyProvenance?.edges.find((entry) => entry.edgeIndex === edgeIndex) ?? null;
}

/**
 * 解析顶点出处项。
 *
 * 设计说明：
 * 1. 顶点坐标签名同样会随尺寸变化漂移；
 * 2. Help Point 依赖稳定顶点身份，因此必须支持按拓扑索引兜底；
 * 3. 当前快照仍输出最新顶点 ref，保证定义重写后落到最新几何。
 *
 * @param topologyProvenance 拓扑出处映射。
 * @param persistentRef 当前顶点几何 ref。
 * @param vertexIndex 当前顶点索引。
 * @returns 命中的出处项；未命中时返回 `null`。
 */
function resolveVertexProvenanceEntry(
  topologyProvenance: SerializedTopologyProvenanceMap | null,
  persistentRef: string,
  vertexIndex: number,
) {
  return topologyProvenance?.vertices?.find((entry) => (
    entry.ref === persistentRef
  )) ?? topologyProvenance?.vertices?.find((entry) => entry.vertexIndex === vertexIndex) ?? null;
}

/**
 * 从同一几何 ref 的候选中消费一个尚未使用的 provenance 项。
 *
 * 同端点边、同位置顶点和同签名面都可能对应不同 Puppy CAD 内核 拓扑项。匹配时先保留仍唯一可用的
 * 已有身份，再按 Puppy CAD 内核 索引和候选顺序兜底，保证每个 provenance 身份最多被消费一次。
 *
 * @param entries 当前拓扑类型的 provenance 项。
 * @param resolveEntryIndex provenance 项索引读取器。
 * @returns 单次消费匹配器；每个 provenance 身份最多返回一次。
 */
function createTopologyProvenanceEntryMatcher<
  TEntry extends { ref: string; subEntityId?: string },
>(
  entries: readonly TEntry[],
  resolveEntryIndex: (entry: TEntry) => number,
): (
  persistentRef: string,
  currentSubEntityId: string,
  topologyIndex: number,
) => (TEntry & { subEntityId: string }) | null {
  const identityEntries = entries as readonly (TEntry & { subEntityId: string })[];
  const entriesByRef = new Map<string, (TEntry & { subEntityId: string })[]>();
  const entryByIndex = new Map<number, TEntry & { subEntityId: string }>();
  const entryByRefAndSubEntityId = new Map<
    string,
    Map<string, TEntry & { subEntityId: string }>
  >();
  const entryByRefAndIndex = new Map<
    string,
    Map<number, TEntry & { subEntityId: string }>
  >();
  identityEntries.forEach((entry) => {
    const geometryEntries = entriesByRef.get(entry.ref) ?? [];
    geometryEntries.push(entry);
    entriesByRef.set(entry.ref, geometryEntries);
    const entryIndex = resolveEntryIndex(entry);
    entryByIndex.set(entryIndex, entry);
    const entriesBySubEntityId = entryByRefAndSubEntityId.get(entry.ref)
      ?? new Map<string, TEntry & { subEntityId: string }>();
    entriesBySubEntityId.set(entry.subEntityId, entry);
    entryByRefAndSubEntityId.set(entry.ref, entriesBySubEntityId);
    const entriesByTopologyIndex = entryByRefAndIndex.get(entry.ref)
      ?? new Map<number, TEntry & { subEntityId: string }>();
    entriesByTopologyIndex.set(entryIndex, entry);
    entryByRefAndIndex.set(entry.ref, entriesByTopologyIndex);
  });
  entriesByRef.forEach((geometryEntries) => {
    geometryEntries.sort((left, right) => resolveEntryIndex(left) - resolveEntryIndex(right));
  });

  const usedSubEntityIds = new Set<string>();
  const nextCandidateIndexByRef = new Map<string, number>();
  return (persistentRef, currentSubEntityId, topologyIndex) => {
    const geometryEntries = entriesByRef.get(persistentRef) ?? [];
    const identityEntry = entryByRefAndSubEntityId.get(persistentRef)?.get(currentSubEntityId);
    const topologyIndexEntry = entryByRefAndIndex.get(persistentRef)?.get(topologyIndex);
    let candidateCursor = nextCandidateIndexByRef.get(persistentRef) ?? 0;
    while (
      candidateCursor < geometryEntries.length
      && usedSubEntityIds.has(geometryEntries[candidateCursor]!.subEntityId)
    ) {
      candidateCursor += 1;
    }
    nextCandidateIndexByRef.set(persistentRef, candidateCursor);
    const orderedGeometryEntry = geometryEntries[candidateCursor];
    const indexedEntry = entryByIndex.get(topologyIndex);
    const matchedEntry = identityEntry && !usedSubEntityIds.has(identityEntry.subEntityId)
      ? identityEntry
      : topologyIndexEntry && !usedSubEntityIds.has(topologyIndexEntry.subEntityId)
        ? topologyIndexEntry
        : orderedGeometryEntry && !usedSubEntityIds.has(orderedGeometryEntry.subEntityId)
          ? orderedGeometryEntry
          : indexedEntry && !usedSubEntityIds.has(indexedEntry.subEntityId)
            ? indexedEntry
            : null;
    if (matchedEntry) {
      usedSubEntityIds.add(matchedEntry.subEntityId);
      if (matchedEntry === orderedGeometryEntry) {
        nextCandidateIndexByRef.set(persistentRef, candidateCursor + 1);
      }
    }
    return matchedEntry;
  };
}

/**
 * 用 topology provenance 校正序列化权威快照的子拓扑身份。
 *
 * 该入口同时服务 Worker 新结果与后端/离线包恢复结果。几何 ref 只用于定位候选，
 * 最终通过“一项只消费一次”保持 Puppy CAD 内核 拓扑项与 `subEntityId` 一对一，禁止因同端点边而复用 ID。
 *
 * @param snapshot 待校正的权威拓扑快照。
 * @param metadata 携带 topology provenance 的结果元数据。
 * @returns 身份与 provenance 对齐后的权威拓扑快照。
 */
export function reconcileTopologySnapshotIdentities(
  snapshot: TopologySnapshot,
  metadata: Record<string, unknown> | undefined,
): TopologySnapshot {
  const topologyProvenance = resolveTopologyProvenance(metadata);
  if (!topologyProvenance) {
    return snapshot;
  }

  const matchFaceProvenance = createTopologyProvenanceEntryMatcher(
    topologyProvenance.faces,
    (entry) => entry.faceIndex,
  );
  const faces = snapshot.faces.map((face) => {
    const provenanceFace = matchFaceProvenance(
      face.ref,
      face.subEntityId,
      face.index,
    );
    if (!provenanceFace) {
      return face;
    }
    return {
      ...face,
      subEntityId: provenanceFace.subEntityId,
      semanticName: provenanceFace.semanticName ?? face.semanticName,
      owningFeatureId: provenanceFace.generatingFeatureId ?? face.owningFeatureId,
    };
  });

  const matchEdgeProvenance = createTopologyProvenanceEntryMatcher(
    topologyProvenance.edges,
    (entry) => entry.edgeIndex,
  );
  const edges = snapshot.edges.map((edge) => {
    const provenanceEdge = matchEdgeProvenance(
      edge.ref,
      edge.subEntityId,
      edge.index,
    );
    if (!provenanceEdge) {
      return edge;
    }
    return {
      ...edge,
      subEntityId: provenanceEdge.subEntityId,
      semanticName: provenanceEdge.semanticName ?? edge.semanticName,
      owningFeatureId: provenanceEdge.generatingFeatureId ?? edge.owningFeatureId,
    };
  });

  const provenanceVertices = topologyProvenance.vertices ?? [];
  const matchVertexProvenance = createTopologyProvenanceEntryMatcher(
    provenanceVertices,
    (entry) => entry.vertexIndex,
  );
  const vertices = snapshot.vertices.map((vertex) => {
    const provenanceVertex = matchVertexProvenance(
      vertex.ref,
      vertex.subEntityId,
      vertex.index,
    );
    return provenanceVertex
      ? { ...vertex, subEntityId: provenanceVertex.subEntityId }
      : vertex;
  });

  return {
    ...snapshot,
    faces,
    edges,
    vertices,
  };
}

/**
 * 解析闰中面的稳定身份。
 *
 * @param faceTriangleRanges 面范围集合。
 * @param meshData 网格数据。
 * @param topologyProvenance 拓扑出处映射。
 * @returns 面身份记录集合。
 */
function resolveFaceIdentityRecords(
  faceTriangleRanges: [number, number][],
  meshData: Pick<MeshData, 'positions' | 'indices'>,
  topologyProvenance: SerializedTopologyProvenanceMap | null,
): FaceIdentityRecord[] {
  return faceTriangleRanges.map((triangleRange, faceIndex) => {
    const localPoint = buildFacePoint(meshData, triangleRange);
    const localNormal = buildFaceNormal(meshData, triangleRange[0]);
    const stablePersistentRef = buildTopologyFaceRef({
      point: localPoint,
      normal: localNormal,
    });
    const provenanceFace = resolveFaceProvenanceEntry(
      topologyProvenance,
      stablePersistentRef,
      faceIndex,
    );
    return {
      faceIndex,
      triangleRange,
      localPoint,
      localNormal,
      persistentRef: stablePersistentRef,
      subEntityId: provenanceFace?.subEntityId ?? buildTopologySubEntityId('face', stablePersistentRef),
      semanticName: provenanceFace?.semanticName,
      owningFeatureId: provenanceFace?.generatingFeatureId ?? null,
    };
  });
}

/**
 * 通过 Puppy CAD 内核 真实拓扑解析面身份记录。
 *
 * @param faceTriangleRanges 面三角形范围。
 * @param meshData 网格数据。
 * @param topologyProvenance 拓扑出处映射。
 * @param puppy Puppy CAD 内核 实例。
 * @param shape 正式结果形体。
 * @returns 面身份记录；无法建立 Puppy CAD 内核 对齐时返回 `null`。
 */
function resolveFaceIdentityRecordsFromPuppyKernel(
  faceTriangleRanges: [number, number][],
  meshData: Pick<MeshData, 'positions' | 'indices'>,
  topologyProvenance: SerializedTopologyProvenanceMap | null,
  puppy: PuppyKernel,
  shape: unknown,
  resolvedFaceShapes?: unknown[],
): {
  faceShapes: unknown[];
  faceRecords: FaceIdentityRecord[];
} | null {
  const faceShapes = resolvedFaceShapes ?? deduplicateShapeItems(enumerateShapeItemsFromPuppyKernel(puppy, shape, 'face'));
  if (faceShapes.length !== faceTriangleRanges.length) {
    return null;
  }

  return {
    faceShapes,
    faceRecords: faceShapes.map((faceShape, faceIndex) => {
      const triangleRange = faceTriangleRanges[faceIndex]!;
      const faceSignature = resolveFaceSignatureFromPuppyKernel(puppy, faceShape);
      const localPoint = faceSignature?.point ?? buildFacePoint(meshData, triangleRange);
      const localNormal = faceSignature?.normal ?? buildFaceNormal(meshData, triangleRange[0]);
      const stablePersistentRef = buildTopologyFaceRef({
        point: localPoint,
        normal: localNormal,
      });
      const provenanceFace = resolveFaceProvenanceEntry(
        topologyProvenance,
        stablePersistentRef,
        faceIndex,
      );
      return {
        faceIndex,
        triangleRange,
        localPoint,
        localNormal,
        persistentRef: stablePersistentRef,
        subEntityId: provenanceFace?.subEntityId ?? buildTopologySubEntityId('face', stablePersistentRef),
        semanticName: provenanceFace?.semanticName,
        owningFeatureId: provenanceFace?.generatingFeatureId ?? null,
        shapeHash: resolveShapeHash(faceShape),
      };
    }),
  };
}

/**
 * 通过 Puppy CAD 内核 真实拓扑构建权威快照。
 *
 * 设计说明：
 * 1. 面三角范围继续来自 Tessellator，因为这是主线程构建面代理几何必须消费的三角区间；
 * 2. 但边、点、面之间的邻接关系和稳定身份必须来自 Puppy CAD 内核 真拓扑，而不是三角网格反推；
 * 3. 一旦 Puppy CAD 内核 面数量与面三角范围数量不一致，就回退到旧的网格推导路径，避免把错误快照传播到主线程。
 *
 * @param meshData 网格数据。
 * @param puppy Puppy CAD 内核 实例。
 * @param shape 正式结果形体。
 * @returns 权威拓扑快照；无法按 Puppy CAD 内核 构建时返回 `null`。
 */
function buildAuthorityTopologySnapshotFromPuppyKernel(
  meshData: Pick<MeshData, 'positions' | 'indices' | 'faceTriangleRanges' | 'metadata'>,
  puppy: PuppyKernel,
  shape: unknown,
): TopologySnapshot | null {
  const vertexCount = Math.floor(meshData.positions.length / 3);
  if (vertexCount < 3) {
    return null;
  }

  const triangleCount = meshData.indices
    ? Math.floor(meshData.indices.length / 3)
    : Math.floor(vertexCount / 3);
  if (triangleCount <= 0) {
    return null;
  }

  const faceShapes = deduplicateShapeItems(enumerateShapeItemsFromPuppyKernel(puppy, shape, 'face'));
  if (faceShapes.length === 0) {
    return null;
  }

  const faceTriangleRanges = normalizeFaceRangesForPuppyKernel(
    meshData.faceTriangleRanges,
    triangleCount,
    faceShapes.length,
  );
  if (faceTriangleRanges.length === 0) {
    return null;
  }

  const topologyProvenance = resolveTopologyProvenance(meshData.metadata);
  const resolvedFaces = resolveFaceIdentityRecordsFromPuppyKernel(
    faceTriangleRanges,
    meshData,
    topologyProvenance,
    puppy,
    shape,
    faceShapes,
  );
  if (!resolvedFaces) {
    return null;
  }

  const { faceShapes: alignedFaceShapes, faceRecords } = resolvedFaces;
  const vertices: TopologySnapshotVertex[] = [];
  const vertexRefToIndex = new Map<string, number>();
  const topologicalEdgeVertexIndices = new Set<number>();

  /**
   * 顶点索引必须以 Puppy CAD 内核 全局顶点枚举为准，再按 ref 做坐标级去重。
   * 这样当 provenance 只能按 vertexIndex 兜底时，索引语义仍和 Puppy CAD 内核 真拓扑对齐。
   */
  deduplicateShapeItems(enumerateShapeItemsFromPuppyKernel(puppy, shape, 'vertex')).forEach((vertexShape, vertexIndex) => {
    const point = extractVertexPointFromPuppyKernel(puppy, vertexShape);
    if (!point) {
      return;
    }
    const persistentRef = buildTopologyVertexRef(point);
    if (vertexRefToIndex.has(persistentRef)) {
      return;
    }
    const provenanceVertex = resolveVertexProvenanceEntry(
      topologyProvenance,
      persistentRef,
      vertexIndex,
    );
    vertexRefToIndex.set(persistentRef, vertices.length);
    vertices.push({
      index: vertices.length,
      role: 'topological',
      subEntityId: provenanceVertex?.subEntityId ?? buildTopologySubEntityId('vertex', persistentRef),
      ref: persistentRef,
      position: point,
    });
  });

  const ensureVertexIndex = (
    point: RawPoint3D,
    fallbackVertexIndex: number,
    role: TopologySnapshotVertexRole,
  ): number => {
    const persistentRef = buildTopologyVertexRef(point);
    const cachedIndex = vertexRefToIndex.get(persistentRef);
    if (typeof cachedIndex === 'number') {
      const cachedVertex = vertices[cachedIndex];
      if (cachedVertex && role === 'curve-reference' && !topologicalEdgeVertexIndices.has(cachedIndex)) {
        vertices[cachedIndex] = { ...cachedVertex, role };
      }
      if (role === 'topological') {
        topologicalEdgeVertexIndices.add(cachedIndex);
      }
      return cachedIndex;
    }
    const provenanceVertex = resolveVertexProvenanceEntry(
      topologyProvenance,
      persistentRef,
      fallbackVertexIndex,
    );
    const nextIndex = vertices.length;
    vertexRefToIndex.set(persistentRef, nextIndex);
    vertices.push({
      index: nextIndex,
      role,
      subEntityId: provenanceVertex?.subEntityId ?? buildTopologySubEntityId('vertex', persistentRef),
      ref: persistentRef,
      position: point,
    });
    if (role === 'topological') {
      topologicalEdgeVertexIndices.add(nextIndex);
    }
    return nextIndex;
  };

  const edgeShapes = deduplicateShapeItems(enumerateShapeItemsFromPuppyKernel(puppy, shape, 'edge'));
  const edges: TopologySnapshotEdge[] = [];
  const faceToEdgeIndices = new Map<number, number[]>();
  const alignedFaceEdgeShapes = alignedFaceShapes.map((faceShape) => (
    deduplicateShapeItems(enumerateShapeItemsFromPuppyKernel(puppy, faceShape, 'edge'))
  ));

  edgeShapes.forEach((edgeShape, edgeIndex) => {
    const edgeVertices = extractEdgeVerticesFromPuppyKernel(puppy, edgeShape);
    const sampledPoints = sampleEdgeCurveFromPuppyKernel(puppy, edgeShape);
    const edgeReferencePoints = resolveEdgeReferencePoints(edgeVertices, sampledPoints);
    if (!edgeReferencePoints) {
      return;
    }
    const persistentRef = buildTopologyEdgeRef(edgeReferencePoints[0].point, edgeReferencePoints[1].point);
    const provenanceEdge = resolveEdgeProvenanceEntry(
      topologyProvenance,
      persistentRef,
      edgeIndex,
    );
    const adjacentFaceIndices = alignedFaceEdgeShapes.reduce<number[]>((matched, faceEdges, faceIndex) => {
      if (faceEdges.some((candidate) => areShapesEquivalent(candidate, edgeShape))) {
        matched.push(faceIndex);
      }
      return matched;
    }, []);
    const adjacentFaceShapes = adjacentFaceIndices
      .map((faceIndex) => alignedFaceShapes[faceIndex])
      .filter((faceShape) => Boolean(faceShape));
    if (isClosedSeamEdgeAcrossFacesFromPuppyKernel(puppy, edgeShape, adjacentFaceShapes)) {
      /*
       * Puppy CAD 内核 seam edge 是闭合曲面的内部参数边，不是 SolidWorks 暴露给用户的真实模型边。
       * 在权威拓扑快照处过滤，可以让选择、显示、特征绑定同时得到一致结果。
       */
      return;
    }
    const owningFaceRefs = adjacentFaceIndices
      .map((faceIndex) => faceRecords[faceIndex]?.persistentRef ?? null)
      .filter((faceRef): faceRef is string => typeof faceRef === 'string' && faceRef.length > 0);
    const vertexAIndex = ensureVertexIndex(
      edgeReferencePoints[0].point,
      edgeIndex * 2,
      edgeReferencePoints[0].role,
    );
    const vertexBIndex = ensureVertexIndex(
      edgeReferencePoints[1].point,
      (edgeIndex * 2) + 1,
      edgeReferencePoints[1].role,
    );
    const snapshotEdge: TopologySnapshotEdge = {
      index: edges.length,
      subEntityId: provenanceEdge?.subEntityId ?? buildTopologySubEntityId('edge', persistentRef),
      semanticName: provenanceEdge?.semanticName,
      ref: persistentRef,
      shapeHash: resolveShapeHash(edgeShape),
      owningFeatureId: provenanceEdge?.generatingFeatureId ?? null,
      owningFaceRefs,
      adjacentFaceIndices,
      curveKind: resolveEdgeCurveKindFromPuppyKernel(puppy, edgeShape),
      vertexAIndex,
      vertexBIndex,
    };
    if (sampledPoints && sampledPoints.length > 2) {
      snapshotEdge.sampledPoints = sampledPoints;
    }
    adjacentFaceIndices.forEach((faceIndex) => {
      const edgeIndices = faceToEdgeIndices.get(faceIndex) ?? [];
      edgeIndices.push(snapshotEdge.index);
      faceToEdgeIndices.set(faceIndex, edgeIndices);
    });
    edges.push(snapshotEdge);
  });

  const faces: TopologySnapshotFace[] = faceRecords.map((faceRecord) => ({
    index: faceRecord.faceIndex,
    subEntityId: faceRecord.subEntityId,
    semanticName: faceRecord.semanticName,
    ref: faceRecord.persistentRef,
    shapeHash: faceRecord.shapeHash,
    owningFeatureId: faceRecord.owningFeatureId,
    adjacentEdgeIndices: [...(faceToEdgeIndices.get(faceRecord.faceIndex) ?? [])].sort((left, right) => left - right),
    adjacentFaceIndices: edges
      .filter((edge) => edge.adjacentFaceIndices.includes(faceRecord.faceIndex))
      .flatMap((edge) => edge.adjacentFaceIndices)
      .filter((adjacentFaceIndex, adjacentIndex, source) => (
        adjacentFaceIndex !== faceRecord.faceIndex && source.indexOf(adjacentFaceIndex) === adjacentIndex
      ))
      .sort((left, right) => left - right),
    triangleRange: faceRecord.triangleRange,
    localNormal: faceRecord.localNormal,
    localPoint: faceRecord.localPoint,
    surfaceKind: resolveFaceSurfaceKindFromPuppyKernel(puppy, alignedFaceShapes[faceRecord.faceIndex]),
  }));

  return reconcileTopologySnapshotIdentities({
    vertices,
    edges,
    faces,
  }, meshData.metadata);
}

/**
 * 仅从 provenance 构建面级 owner 快照。
 *
 * 设计说明：
 * 1. 曲线 profile 结果禁止启用 mesh fallback，否则三角剖分边会被误当成 topo 边显示；
 * 2. 但相交合并体的设计树特征高亮只需要知道哪些面属于哪个 feature；
 * 3. 因此在 Puppy CAD 内核 对齐失败且 metadata 已带 provenance 时，只回填 face ownership，不生成任何网格推导边。
 *
 * @param meshData 网格数据。
 * @param topologyProvenance 拓扑出处映射。
 * @returns 面级 owner 快照；没有有效 owner 时返回 `null`。
 */
function buildFaceOwnerSnapshotFromProvenance(
  meshData: Pick<MeshData, 'positions' | 'indices' | 'faceTriangleRanges' | 'metadata'>,
  topologyProvenance: SerializedTopologyProvenanceMap | null,
): TopologySnapshot | null {
  if (!topologyProvenance?.faces || topologyProvenance.faces.length === 0) {
    return null;
  }

  const vertexCount = Math.floor(meshData.positions.length / 3);
  if (vertexCount < 3) {
    return null;
  }

  const triangleCount = meshData.indices
    ? Math.floor(meshData.indices.length / 3)
    : Math.floor(vertexCount / 3);
  if (triangleCount <= 0) {
    return null;
  }

  const faceTriangleRanges = normalizeFaceRanges(meshData.faceTriangleRanges, triangleCount);
  if (faceTriangleRanges.length === 0) {
    return null;
  }

  const faceIdentityRecords = resolveFaceIdentityRecords(
    faceTriangleRanges,
    meshData,
    topologyProvenance,
  );
  const faces: TopologySnapshotFace[] = faceIdentityRecords.map((faceRecord) => ({
    index: faceRecord.faceIndex,
    subEntityId: faceRecord.subEntityId,
    semanticName: faceRecord.semanticName,
    ref: faceRecord.persistentRef,
    owningFeatureId: faceRecord.owningFeatureId,
    adjacentEdgeIndices: [],
    adjacentFaceIndices: [],
    triangleRange: faceRecord.triangleRange,
    localNormal: faceRecord.localNormal,
    localPoint: faceRecord.localPoint,
  }));

  if (!faces.some((face) => typeof face.owningFeatureId === 'string' && face.owningFeatureId.length > 0)) {
    return null;
  }

  return reconcileTopologySnapshotIdentities({
    vertices: [],
    edges: [],
    faces,
  }, meshData.metadata);
}

/**
 * 构建权威拓扑快照。
 *
 * 设计说明：
 * 1. 该函数在 worker 侧根据已网格化结果构建“可传输的权威拓扑快照”；
 * 2. Viewer 只能消费这里的输出，不再从三角网格自行反推业务真相；
 * 3. 如果 metadata 中已携带出处映射，则优先复用出处映射中的稳定引用和 owningFeatureId。
 * 4. 曲线 profile 结果禁止把三角剖分边伪装成 topo 边，否则重建后曲面会出现密集黑边。
 *
 * @param meshData 网格数据。
 * @param options 拓扑构建选项。
 * @returns 权威拓扑快照；网格不合法时返回 `null`。
 */
export function buildAuthorityTopologySnapshot(
  meshData: Pick<MeshData, 'positions' | 'indices' | 'faceTriangleRanges' | 'metadata'>,
  options: AuthorityTopologyBuildOptions = {},
): TopologySnapshot | null {
  if (options.puppy && options.shape) {
    const kernelSnapshot = buildAuthorityTopologySnapshotFromPuppyKernel(
      meshData,
      options.puppy,
      options.shape,
    );
    if (kernelSnapshot) {
      return kernelSnapshot;
    }
  }

  const topologyProvenance = resolveTopologyProvenance(meshData.metadata);
  if (options.allowMeshFallback === false) {
    return buildFaceOwnerSnapshotFromProvenance(meshData, topologyProvenance);
  }

  const vertexCount = Math.floor(meshData.positions.length / 3);
  if (vertexCount < 3) {
    return null;
  }

  const triangleCount = meshData.indices
    ? Math.floor(meshData.indices.length / 3)
    : Math.floor(vertexCount / 3);
  if (triangleCount <= 0) {
    return null;
  }

  const faceTriangleRanges = normalizeFaceRanges(meshData.faceTriangleRanges, triangleCount);
  if (faceTriangleRanges.length === 0) {
    return null;
  }

  const faceIdentityRecords = resolveFaceIdentityRecords(
    faceTriangleRanges,
    meshData,
    topologyProvenance,
  );
  const triangleFaceIndices = new Array<number>(triangleCount).fill(-1);
  faceTriangleRanges.forEach(([start, end], faceIndex) => {
    for (let triangleIndex = start; triangleIndex < end; triangleIndex += 1) {
      triangleFaceIndices[triangleIndex] = faceIndex;
    }
  });

  const vertices: TopologySnapshotVertex[] = [];
  const rawVertexToTopologyIndex = new Array<number>(vertexCount).fill(-1);
  const vertexKeyToIndex = new Map<string, number>();
  for (let rawIndex = 0; rawIndex < vertexCount; rawIndex += 1) {
    const point = readRawVertexPoint(meshData.positions, rawIndex);
    const vertexKey = buildVertexKey(point);
    let topologyIndex = vertexKeyToIndex.get(vertexKey);
    if (typeof topologyIndex !== 'number') {
      const persistentRef = buildTopologyVertexRef(point);
      topologyIndex = vertices.length;
      const provenanceVertex = resolveVertexProvenanceEntry(
        topologyProvenance,
        persistentRef,
        topologyIndex,
      );
      vertexKeyToIndex.set(vertexKey, topologyIndex);
      vertices.push({
        index: topologyIndex,
        role: 'topological',
        subEntityId: provenanceVertex?.subEntityId ?? buildTopologySubEntityId('vertex', persistentRef),
        ref: persistentRef,
        position: point,
      });
    }
    rawVertexToTopologyIndex[rawIndex] = topologyIndex;
  }

  const edgeAccumulators = new Map<string, EdgeAccumulator>();
  for (let triangleIndex = 0; triangleIndex < triangleCount; triangleIndex += 1) {
    const faceIndex = triangleFaceIndices[triangleIndex];
    if (faceIndex < 0) {
      continue;
    }
    const topologyA = rawVertexToTopologyIndex[readTriangleVertexRawIndex(meshData, triangleIndex, 0)];
    const topologyB = rawVertexToTopologyIndex[readTriangleVertexRawIndex(meshData, triangleIndex, 1)];
    const topologyC = rawVertexToTopologyIndex[readTriangleVertexRawIndex(meshData, triangleIndex, 2)];
    const triangleEdges: Array<[number, number]> = [
      [topologyA, topologyB],
      [topologyB, topologyC],
      [topologyC, topologyA],
    ];

    triangleEdges.forEach(([vertexAIndex, vertexBIndex]) => {
      const key = vertexAIndex < vertexBIndex
        ? `${vertexAIndex}:${vertexBIndex}`
        : `${vertexBIndex}:${vertexAIndex}`;
      const existing = edgeAccumulators.get(key);
      if (existing) {
        existing.adjacentFaces.add(faceIndex);
        existing.usageByFace.set(faceIndex, (existing.usageByFace.get(faceIndex) ?? 0) + 1);
        return;
      }
      edgeAccumulators.set(key, {
        vertexAIndex: Math.min(vertexAIndex, vertexBIndex),
        vertexBIndex: Math.max(vertexAIndex, vertexBIndex),
        adjacentFaces: new Set([faceIndex]),
        usageByFace: new Map([[faceIndex, 1]]),
      });
    });
  }

  const edges: TopologySnapshotEdge[] = [];
  const faceToEdgeIndices = new Map<number, number[]>();
  Array.from(edgeAccumulators.entries())
    .sort(([left], [right]) => left.localeCompare(right))
    .forEach(([, accumulator]) => {
      const adjacentFaceIndices = Array.from(accumulator.adjacentFaces.values()).sort((left, right) => left - right);
      if (adjacentFaceIndices.length === 1) {
        const usage = accumulator.usageByFace.get(adjacentFaceIndices[0]!) ?? 0;
        if (usage > 1) {
          return;
        }
      }

      const vertexA = vertices[accumulator.vertexAIndex]!;
      const vertexB = vertices[accumulator.vertexBIndex]!;
      const persistentRef = buildTopologyEdgeRef(vertexA.position, vertexB.position);
      const edgeIndex = edges.length;
      const provenanceEdge = resolveEdgeProvenanceEntry(
        topologyProvenance,
        persistentRef,
        edgeIndex,
      );
      const owningFaceRefs = adjacentFaceIndices
        .map((faceIndex) => faceIdentityRecords[faceIndex]?.persistentRef ?? null)
        .filter((faceRef): faceRef is string => typeof faceRef === 'string' && faceRef.length > 0);
      edges.push({
        index: edgeIndex,
        subEntityId: provenanceEdge?.subEntityId ?? buildTopologySubEntityId('edge', persistentRef),
        ref: persistentRef,
        owningFeatureId: provenanceEdge?.generatingFeatureId ?? null,
        owningFaceRefs,
        adjacentFaceIndices,
        vertexAIndex: accumulator.vertexAIndex,
        vertexBIndex: accumulator.vertexBIndex,
      });
      adjacentFaceIndices.forEach((faceIndex) => {
        const edgeIndices = faceToEdgeIndices.get(faceIndex) ?? [];
        edgeIndices.push(edgeIndex);
        faceToEdgeIndices.set(faceIndex, edgeIndices);
      });
    });

  const faces: TopologySnapshotFace[] = faceIdentityRecords.map((faceRecord) => {
    return {
      index: faceRecord.faceIndex,
      subEntityId: faceRecord.subEntityId,
      semanticName: faceRecord.semanticName,
      ref: faceRecord.persistentRef,
      owningFeatureId: faceRecord.owningFeatureId,
      adjacentEdgeIndices: [...(faceToEdgeIndices.get(faceRecord.faceIndex) ?? [])].sort((left, right) => left - right),
      adjacentFaceIndices: edges
        .filter((edge) => edge.adjacentFaceIndices.includes(faceRecord.faceIndex))
        .flatMap((edge) => edge.adjacentFaceIndices)
        .filter((adjacentFaceIndex, adjacentIndex, source) => (
          adjacentFaceIndex !== faceRecord.faceIndex && source.indexOf(adjacentFaceIndex) === adjacentIndex
        ))
        .sort((left, right) => left - right),
      triangleRange: faceRecord.triangleRange,
      localNormal: faceRecord.localNormal,
      localPoint: faceRecord.localPoint,
    };
  });

  return reconcileTopologySnapshotIdentities({
    vertices,
    edges,
    faces,
  }, meshData.metadata);
}
